import { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { Box, Card, Heading, Image, Pagination } from '@zillow/constellation-10';
import { LoadingButton, LoadingHeading, LoadingText } from '@zg-rentals/loading-components';

import { Lease } from '../models';
import NotEndedLease from './TenantDashboard/NotEndedLease';
import { PAGINATION_NUMBER_OF_RESULTS } from '../constants/stringConstants';
import DocumentSignature from '../../../images/documentSignature.svg';
import { usePaginatedLeasesForTenant, usePropertiesInfoQueries } from '../utils/queries';

function LeaseCardSkeleton() {
  return (
    <Card tone="neutral" elevated={false}>
      <LoadingHeading
        isLoading
        level={2}
        loadingWidth="15rem"
        css={{
          textStyle:
            'responsive.heading-xs' /* oxlint-disable-line constellation/no-unknown-token -- valid textStyle; rule can't check textStyle (JSINFRA-3580) */,
          marginBottom: 'tight',
        }}
      >
        Loading
      </LoadingHeading>
      <LoadingText isLoading loadingWidth="15rem" css={{ marginBottom: 'tightest' }}>
        Loading
      </LoadingText>
      <LoadingText isLoading loadingWidth="18rem" css={{ marginBottom: 'tight' }}>
        Loading
      </LoadingText>
      <Box css={{ marginTop: 'tight' }}>
        <LoadingHeading
          isLoading
          level={3}
          css={{
            textStyle:
              'responsive.body-lg-bold' /* oxlint-disable-line constellation/no-unknown-token -- valid textStyle; rule can't check textStyle (JSINFRA-3580) */,
            marginBottom: 'tighter',
          }}
          loadingWidth="10rem"
        >
          Loading
        </LoadingHeading>
        <Card tone="neutral" elevated={false}>
          <LoadingHeading
            isLoading
            level={4}
            loadingWidth="15rem"
            css={{
              textStyle:
                'responsive.body-lg-bold' /* oxlint-disable-line constellation/no-unknown-token -- valid textStyle; rule can't check textStyle (JSINFRA-3580) */,
              marginBottom: 'tighter',
            }}
          >
            Loading
          </LoadingHeading>
          <LoadingText isLoading loadingWidth="8rem">
            Loading
          </LoadingText>
        </Card>
      </Box>
      <Box css={{ marginTop: 'tight' }}>
        <LoadingHeading
          isLoading
          level={4}
          css={{
            textStyle:
              'responsive.heading-xs' /* oxlint-disable-line constellation/no-unknown-token -- valid textStyle; rule can't check textStyle (JSINFRA-3580) */,
          }}
          loadingWidth="8rem"
        >
          Loading
        </LoadingHeading>
        <LoadingText isLoading loadingWidth="12rem">
          Loading
        </LoadingText>
      </Box>
      <Box css={{ marginTop: 'tight' }}>
        <LoadingButton isLoading loadingWidth="10rem">
          View lease
        </LoadingButton>
      </Box>
    </Card>
  );
}

export function TenantDashboard() {
  const history = useHistory();
  const [pageNumber, setPageNumber] = useState(0);
  const [initializedUpsells, setInitializedUpsells] = useState<Set<string>>(new Set());

  const { data, isLoading: isLeasesLoading } = usePaginatedLeasesForTenant({
    numResults: PAGINATION_NUMBER_OF_RESULTS,
    offset: pageNumber * PAGINATION_NUMBER_OF_RESULTS,
  });

  const totalNumberOfLeases = data?.totalNumberOfLeases ?? 0;
  const leases = useMemo(() => (data?.leases || []).map((lease) => new Lease(lease)), [data?.leases]);

  const listingAliases = useMemo(() => leases.map((lease) => lease.listingAlias), [leases]);
  const { propertiesById: propertiesByListingAlias, queries: propertyQueries } =
    usePropertiesInfoQueries(listingAliases);
  const isPropertiesLoading = propertyQueries.some((query) => query.isLoading);

  // Check if all upsells have been initialized
  // If there are no leases, consider all upsells as initialized
  const allUpsellsInitialized = leases.every((lease) => initializedUpsells.has(lease.id));

  // Reset initialized upsells when page changes
  useEffect(() => {
    setInitializedUpsells(new Set());
  }, [pageNumber]);

  // Safety timeout: force show content after 5 seconds even if upsells haven't loaded
  // This prevents the dashboard from being stuck in loading state if upsells fail
  useEffect(() => {
    if (leases.length > 0 && !allUpsellsInitialized) {
      const timeout = setTimeout(() => {
        // Mark all leases as initialized
        setInitializedUpsells(new Set(leases.map((lease) => lease.id)));
      }, 5000);

      return () => clearTimeout(timeout);
    }
  }, [leases, allUpsellsInitialized]);

  const gotoLeaseSummary = (lease: Lease) => {
    history.push(`${lease.getSummaryPath()}${window.location.search}`);
  };

  const handleUpsellInitialized = (leaseId: string) => {
    setInitializedUpsells((prev) => new Set(prev).add(leaseId));
  };

  const numberOfPages = Math.ceil(totalNumberOfLeases / PAGINATION_NUMBER_OF_RESULTS);

  const showSkeleton = (isLeasesLoading && !data) || (data && isPropertiesLoading);

  if (showSkeleton) {
    return (
      <>
        {Array.from({ length: 5 }).map((_, index) => (
          <Box key={index} css={{ marginBottom: index === 4 ? 'none' : 'tight' }}>
            <LeaseCardSkeleton />
          </Box>
        ))}
      </>
    );
  }

  if (leases == null || leases.length === 0) {
    return (
      <Box css={{ textAlign: 'left' }}>
        <Box aria-hidden="true" css={{ marginBottom: 'default' }}>
          <Image src={DocumentSignature} css={{ width: '120px', height: '120px' }} alt="" role="presentation" />
        </Box>
        <Heading level={2} textStyle="heading-xs">
          You currently have no leases
        </Heading>
      </Box>
    );
  }

  return (
    <>
      {!allUpsellsInitialized && (
        <>
          {Array.from({ length: leases.length }).map((_, index) => (
            <Box key={index} css={{ marginBottom: index === leases.length - 1 ? 'none' : 'tight' }}>
              <LeaseCardSkeleton />
            </Box>
          ))}
        </>
      )}
      <Box
        css={{
          visibility: allUpsellsInitialized ? 'visible' : 'hidden',
          height: allUpsellsInitialized ? 'auto' : '0',
          overflow: 'hidden',
        }}
      >
        {leases.map((lease: Lease, index: number) => (
          <Box key={lease.id} css={{ marginBottom: index === leases.length - 1 ? 'none' : 'tight' }}>
            <NotEndedLease
              gotoLeaseSummary={gotoLeaseSummary}
              lease={lease}
              property={propertiesByListingAlias[lease.listingAlias]}
              onUpsellInitialized={handleUpsellInitialized}
            />
          </Box>
        ))}
      </Box>
      {allUpsellsInitialized && numberOfPages > 1 && (
        <Pagination
          css={{ marginTop: 'tight' }}
          currentPageIndex={pageNumber}
          totalPages={numberOfPages}
          onPageSelected={(pageIndex) => {
            setPageNumber(pageIndex);
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
        />
      )}
    </>
  );
}

export default TenantDashboard;
